import { Handle, Position } from "@xyflow/react";
import { Server, User } from "lucide-react";
import type { Actor } from "@/lib/arch-parser";
import { cn } from "@/lib/utils";
import { CommentBadge } from "./CommentBadge";
import { kindPalette } from "./node-colors";

export interface ActorNodeData {
  actor: Actor;
  faded?: boolean;
  highlighted?: boolean;
  commentCount?: number;
  carryOverCount?: number;
  compact?: boolean;
  [key: string]: unknown;
}

export function ActorNode({ data, selected }: { data: ActorNodeData; selected?: boolean }) {
  const palette = kindPalette("actor");
  const Icon = data.actor.type === "system" ? Server : User;
  const caption = data.actor.headline ?? data.actor.description;
  return (
    <div
      className={cn(
        "relative rounded-2xl border-[1.5px] px-4 py-3 shadow-sm transition-all min-w-[150px] max-w-[200px] text-center",
        palette.bg,
        palette.border,
        palette.text,
        selected &&
          "ring-2 ring-offset-2 ring-offset-background ring-[color:var(--node-selection-ring)] shadow-md z-10",
        data.faded && "opacity-30",
        data.highlighted && "ring-2 ring-teal-400/70",
      )}
      title={data.compact && caption ? caption : undefined}
    >
      <div
        className="absolute -top-3.5 left-2 uppercase tracking-eyebrow font-semibold text-muted-foreground whitespace-nowrap pointer-events-none"
        style={{ fontSize: "calc(9px * var(--archviz-label-scale, 1))" }}
      >
        {data.actor.type === "system" ? "Actor · System" : "Actor"}
      </div>
      <CommentBadge count={data.commentCount ?? 0} carryOver={data.carryOverCount ?? 0} />
      <Handle type="target" position={Position.Top} id="top" className="!bg-foreground/30" />
      <Handle type="target" position={Position.Bottom} id="bottom" className="!bg-foreground/30" />
      <Handle type="target" position={Position.Right} id="right" className="!bg-foreground/30" />
      <Handle type="target" position={Position.Left} id="left" className="!bg-foreground/30" />
      <Icon className="h-5 w-5 mx-auto mb-1 opacity-80" />
      <div
        className="font-semibold leading-none tracking-tight"
        style={{ fontSize: "calc(14px * var(--archviz-label-scale, 1))" }}
      >
        {data.actor.label}
      </div>
      {!data.compact && caption && (
        <div
          className="text-muted-foreground leading-snug mt-0.5"
          style={{ fontSize: "calc(10px * var(--archviz-label-scale, 1))" }}
        >
          {caption}
        </div>
      )}
      <Handle type="source" position={Position.Top} id="top-src" className="!bg-foreground/30" />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom-src"
        className="!bg-foreground/30"
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right-src"
        className="!bg-foreground/30"
      />
      <Handle type="source" position={Position.Left} id="left-src" className="!bg-foreground/30" />
    </div>
  );
}
